import React, { useState, useEffect } from 'react';
import { useConsent } from '../../contexts/ConsentContext';
import type { ConsentPreferences } from '../../contexts/ConsentContext';
import styles from './ConsentBanner.module.css';

const REJECT_ALL_CONSENT: ConsentPreferences = {
  essential: true,
  analytics: false,
  marketing: false,
  location: false,
  notifications: false,
  dataSharing: false,
  personalization: false
};

const ACCEPT_ALL_CONSENT: ConsentPreferences = {
  essential: true,
  analytics: true,
  marketing: true,
  location: true,
  notifications: true,
  dataSharing: true,
  personalization: true
};

const CUSTOMIZE_OPTIONS: { key: keyof ConsentPreferences; title: string; description: string; disabled?: boolean }[] = [
  { key: 'essential', title: 'Essential', description: 'Required for login, security, and core features.', disabled: true },
  { key: 'analytics', title: 'Analytics & performance', description: 'Usage data to improve the platform.' },
  { key: 'marketing', title: 'Marketing', description: 'Promotions, newsletters, and product updates.' },
  { key: 'location', title: 'Location', description: 'Location-based features and load matching.' },
  { key: 'notifications', title: 'Notifications', description: 'Alerts for loads, shipments, and updates.' },
  { key: 'dataSharing', title: 'Data sharing', description: 'Share profile with partners for matching.' },
  { key: 'personalization', title: 'Personalization', description: 'Tailored experience and recommendations.' }
];

const ConsentBanner: React.FC = () => {
  const { consent, updateConsent, isConsentLoaded } = useConsent();
  const [showBanner, setShowBanner] = useState(false);
  const [showCustomizePanel, setShowCustomizePanel] = useState(false);

  // Show banner only on first visit (no stored preferences yet)
  useEffect(() => {
    if (isConsentLoaded) {
      const hasStoredConsent = localStorage.getItem('tranzit-consent-preferences');
      setShowBanner(!hasStoredConsent);
    }
  }, [isConsentLoaded]);

  const handleAcceptAll = () => {
    updateConsent(ACCEPT_ALL_CONSENT);
    setShowBanner(false);
    setShowCustomizePanel(false);
  };

  const handleRejectAll = () => {
    updateConsent(REJECT_ALL_CONSENT);
    setShowBanner(false);
    setShowCustomizePanel(false);
  };

  const handleCustomizeClick = () => {
    setShowCustomizePanel(true);
  };

  const handleSavePreferences = () => {
    updateConsent(consent);
    setShowBanner(false);
    setShowCustomizePanel(false);
  };

  const handleToggle = (key: keyof ConsentPreferences, value: boolean) => {
    if (key === 'essential') return;
    updateConsent({ ...consent, [key]: value });
  };

  if (!showBanner || !isConsentLoaded) {
    return null;
  }

  return (
    <div className={styles.banner}>
      <div className={styles.bannerContent}>
        {!showCustomizePanel ? (
          <>
            <div className={styles.bannerText}>
              <h3>We value your privacy</h3>
              <p>
                We use cookies and similar technologies to provide, protect, and improve our services.
                You can reject optional cookies, customize your choices, or accept all.
              </p>
            </div>
            <div className={styles.bannerActions}>
              <button type="button" className={styles.rejectButton} onClick={handleRejectAll}>
                Reject all
              </button>
              <button type="button" className={styles.customizeButton} onClick={handleCustomizeClick}>
                Customize
              </button>
              <button type="button" className={styles.acceptButton} onClick={handleAcceptAll}>
                Accept all
              </button>
            </div>
          </>
        ) : (
          <div className={styles.customizePanel}>
            <h3 className={styles.customizeTitle}>Cookie preferences</h3>
            <p className={styles.customizeIntro}>
              Choose which categories you allow. Essential is always on for the platform to work.
            </p>
            <div className={styles.consentList}>
              {CUSTOMIZE_OPTIONS.map(({ key, title, description, disabled }) => (
                <div key={key} className={styles.consentRow}>
                  <div className={styles.consentLabel}>
                    <span className={styles.consentName}>{title}</span>
                    <span className={styles.consentDesc}>{description}</span>
                  </div>
                  <label className={styles.toggleWrap}>
                    <input
                      type="checkbox"
                      checked={consent[key]}
                      disabled={disabled}
                      onChange={(e) => handleToggle(key, e.target.checked)}
                      className={styles.checkbox}
                      aria-label={title}
                    />
                    {disabled && <span className={styles.requiredBadge}>Required</span>}
                  </label>
                </div>
              ))}
            </div>
            <div className={styles.customizeActions}>
              <button type="button" className={styles.rejectButton} onClick={handleRejectAll}>
                Reject all
              </button>
              <button type="button" className={styles.backButton} onClick={() => setShowCustomizePanel(false)}>
                Back
              </button>
              <button type="button" className={styles.saveButton} onClick={handleSavePreferences}>
                Save preferences
              </button>
              <button type="button" className={styles.acceptButton} onClick={handleAcceptAll}>
                Accept all
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConsentBanner;
